import s from './profile-switcher.module.scss'
import { useState, useRef } from 'react'
import classNames from 'classnames'
import { IconChevronUp, IconChevronDown, IconCheck } from '@tabler/icons-react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { useProfile } from '@shared/api/useProfile'
import { auth } from '@shared/api/services'
import { storeActiveProfile } from '@utils/lib/activeProfile'
import { useActiveProfileInfo } from '@entities/profile/lib/use-active-profile-info'
import { useClickOutside } from '@utils/lib/useClickOutside'
import { getAvatarUrl } from '@utils/lib/getAvatarUrl'
import { Modal } from '@shared/ui/modal/modal'
import { Input } from '@shared/ui/input/input'
import { Button } from '@shared/ui/button/button'

// Модалка подтверждения пароля при переключении с детского профиля на родительский
// (постановка «Родители и дети»). Проверка — обычным вызовом логина: при успехе
// auth.login сам обновит токен, сбросит активный профиль и перезагрузит приложение
// в контексте владельца.
const OwnerPasswordModal = ({ isOpen, close }) => {
	const { data: account } = useProfile()
	const methods = useForm({ defaultValues: { password: '' } })
	const [isSubmitting, setIsSubmitting] = useState(false)

	const submit = methods.handleSubmit(({ password }) => {
		setIsSubmitting(true)
		auth.login({ phone: account?.phone, password })
			.then(() => {
				// После успешного логина auth.login сбрасывает активный профиль и
				// перезагружает приложение — помечаем выбор контекста владельца,
				// чтобы гард не отправлял на экран выбора профиля.
				storeActiveProfile(null)
			})
			.catch(() => {
				setIsSubmitting(false)
				toast.error('Неверный пароль')
			})
	})

	const modalStyle = {
		content: {
			width: 'calc(100% - 40px)',
			maxWidth: '420px',
			padding: '20px',
		},
	}

	return (
		<Modal isOpen={isOpen} close={close} title="Вход в аккаунт родителя" style={modalStyle}>
			<FormProvider {...methods}>
				<form className={s.passwordForm} onSubmit={submit}>
					<p className={s.passwordHint}>
						Для перехода в аккаунт родителя введите пароль
					</p>
					<Input
						type="password"
						name="password"
						placeholder="Пароль"
						autoComplete="current-password"
						className={s.input}
						validation={{ required: 'Введите пароль' }}
					/>
					<Button label="Войти" fetching={isSubmitting} />
				</form>
			</FormProvider>
		</Modal>
	)
}

// Переключатель активного профиля (постановка «Родители и дети»).
// Показывается, только если у аккаунта есть детские профили.
// Переключение на родительский из детского — с подтверждением пароля;
// между детьми и из родительского на детский — без пароля.
// menuDirection: 'up' — для низа сайдбара (по умолчанию), 'down' — для верха экрана.
export const ProfileSwitcher = ({ menuDirection = 'up' }) => {
	const { data: account } = useProfile()
	const { profiles, activeProfile, setActiveProfile } = useActiveProfileInfo()
	const [isOpen, setIsOpen] = useState(false)
	const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
	const ref = useRef(null)

	useClickOutside(ref, () => setIsOpen(false))

	if (!profiles.length) {
		return null
	}

	const isOwnerContext = !activeProfile
	const displayName = isOwnerContext ? account?.name : activeProfile?.name
	const displayAvatar = isOwnerContext ? account?.avatar : activeProfile?.avatar

	const select = (profile) => {
		if (profile === null && activeProfile) {
			// Из детского профиля в родительский — только с паролем
			setIsOpen(false)
			setIsPasswordModalOpen(true)
			return
		}

		setActiveProfile(profile)
		setIsOpen(false)
	}

	return (
		<div className={s.wrapper} ref={ref}>
			<button type="button" className={s.button} onClick={() => setIsOpen((prev) => !prev)}>
				<div className={s.buttonAvatar}>
					<img src={getAvatarUrl(displayAvatar)} alt="" />
				</div>
				<div className={s.buttonContent}>
					<span className={s.buttonName}>{displayName}</span>
					<span className={s.buttonRole}>{isOwnerContext ? 'Мой аккаунт' : 'Ребёнок'}</span>
				</div>
				{isOpen ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
			</button>
			{isOpen && (
				<nav className={classNames(s.menu, menuDirection === 'down' && s.menuDown)}>
					<button type="button" className={s.item} onClick={() => select(null)}>
						<div className={s.itemAvatar}>
							<img src={getAvatarUrl(account?.avatar)} alt="" />
						</div>
						<div className={s.itemContent}>
							<span className={s.itemName}>{account?.name}</span>
							<span className={s.itemRole}>Родитель</span>
						</div>
						{isOwnerContext && <IconCheck size={18} className={s.check} />}
					</button>
					{profiles.map((profile) => (
						<button key={profile.id} type="button" className={s.item} onClick={() => select(profile)}>
							<div className={s.itemAvatar}>
								<img src={getAvatarUrl(profile.avatar)} alt="" />
							</div>
							<div className={s.itemContent}>
								<span className={s.itemName}>{profile.name}</span>
								<span className={s.itemRole}>Ребёнок</span>
							</div>
							{activeProfile?.id === profile.id && <IconCheck size={18} className={s.check} />}
						</button>
					))}
				</nav>
			)}
			<OwnerPasswordModal isOpen={isPasswordModalOpen} close={() => setIsPasswordModalOpen(false)} />
		</div>
	)
}
